/*
  VionB — case engraving in the cart drawer (2026-10-01)

  Markup: snippets/vb-engraving-line.liquid (trigger + engraved rows) and
  snippets/vb-engraving-fix.liquid (drift marker). Texts and the addon variant id:
  JSON block #vb-eg-config written by sections/cart-drawer.liquid.

  Data model = the legacy one the warehouse already processes:
  - the name sits on the EARBUDS line as `personalized_text` (that line ships);
  - the bare addon line's quantity = number of engraved units.
  Engrave one unit = add 1 engraved unit + 1 addon, then take 1 plain unit out.
  Remove = add the plain units back, take the engraved ones out, lower the addon.
  Adding first means a failure never loses an earbud.

  Speed (2026-10-01, measured 4.4-5.1 s before): the sheet closes and the
  "Engraved" row shows instantly (optimistic), then 2 requests — add.js, then
  change.js carrying `sections` so the drawer renders from that response (no
  updateCart round trip). If Shopify re-keyed the clicked line in between, the
  slow path re-reads the cart (Shopify splits and re-keys BOGO lines).

  Everything is delegated on document: the drawer HTML is replaced on every cart
  change, so nothing is bound to drawer nodes.
*/
(() => {
  if (window.vbEngraving) return;
  window.vbEngraving = true;

  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const ALLOWED = /[^\p{L}\p{N} .&'’!\-]/gu;
  const ON_CART_PAGE = /\/cart\/?$/.test(window.location.pathname);

  const config = () => {
    try {
      return JSON.parse(document.getElementById('vb-eg-config').textContent);
    } catch (e) {
      return {};
    }
  };

  /* ---------- cart helpers ---------- */

  const post = (url, body) =>
    fetch(ROOT + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((res) =>
      res.json().then((json) => {
        if (!res.ok) throw json;
        return json;
      })
    );

  const getCart = () => fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  const nameOf = (line) => ((line.properties && line.properties.personalized_text) || '').trim();

  // Take `count` units out of the lines matching `match`, the clicked line first.
  // change.js returns the whole cart, so each step works on fresh keys.
  async function takeUnits(cart, match, count, preferKey) {
    while (count > 0) {
      const lines = cart.items.filter(match).sort((a, b) => (b.key === preferKey) - (a.key === preferKey));
      if (!lines.length) break;
      const line = lines[0];
      const take = Math.min(count, line.quantity);
      cart = await post('cart/change.js', { id: line.key, quantity: line.quantity - take });
      count -= take;
    }
    return cart;
  }

  // Addon quantity = engraved units. Returns the (possibly updated) cart.
  async function reconcile(cart) {
    const addon = Number(config().addon);
    if (!addon) return cart;
    const target = cart.items
      .filter((i) => i.variant_id !== addon && nameOf(i))
      .reduce((sum, i) => sum + i.quantity, 0);
    let addons = cart.items.filter((i) => i.variant_id === addon);
    const have = addons.reduce((sum, i) => sum + i.quantity, 0);
    if (have === target) return cart;

    if (!addons.length) {
      await post('cart/add.js', { items: [{ id: addon, quantity: target }] });
      return getCart();
    }
    cart = await post('cart/change.js', { id: addons[0].key, quantity: target });
    // Legacy carts can hold several addon lines: everything beyond the first goes.
    for (;;) {
      addons = cart.items.filter((i) => i.variant_id === addon);
      if (addons.length <= 1) break;
      cart = await post('cart/change.js', { id: addons[addons.length - 1].key, quantity: 0 });
    }
    return cart;
  }

  // The theme's own refresh: posts an empty update with the drawer sections and
  // calls cart-drawer.renderContents(). Same path as a quantity change.
  const refreshDrawer = () => {
    if (ON_CART_PAGE) {
      window.location.reload();
      return;
    }
    if (!document.querySelector('cart-drawer')) return;
    const items = document.querySelector('cart-drawer-items');
    if (items && typeof items.updateCart === 'function') items.updateCart();
    else window.location.reload();
  };

  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  const withSections = (body) => Object.assign(body, { sections: SECTIONS, sections_url: window.location.pathname });

  // Render the drawer straight from a cart API response that carries `sections`:
  // same call the theme makes after updateCart(), minus one round trip.
  function renderFrom(res) {
    const drawer = document.querySelector('cart-drawer');
    if (!ON_CART_PAGE && drawer && res && res.sections && res.sections['cart-drawer'] && typeof drawer.renderContents === 'function') {
      try {
        drawer.renderContents(res, true);
        return;
      } catch (e) {
        console.error('[vb-engraving] render', e);
      }
    }
    refreshDrawer();
  }

  // One cart action at a time: two quick clicks never interleave their requests.
  let queue = Promise.resolve();
  const enqueue = (job) => (queue = queue.then(job, job));

  function toast(message) {
    const el = document.createElement('div');
    el.className = 'vb-eg-toast';
    el.setAttribute('role', 'alert');
    el.textContent = message;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 4000);
  }

  const CHECK =
    '<svg class="vb-eg__check" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // Shown the instant the customer confirms; the server render replaces it.
  function showPending(d, text) {
    const trigger = d.trigger;
    if (!trigger || !trigger.isConnected) return;
    const row = document.createElement('div');
    row.className = 'vb-eg__done is-pending';
    row.innerHTML = `${CHECK}<span class="vb-eg__label">Engraved: <strong></strong> <span class="vb-eg__price"></span></span>`;
    row.querySelector('strong').textContent = `\u201c${text}\u201d`;
    row.querySelector('.vb-eg__price').textContent = d.price ? `+${d.price}` : '';
    if (d.qty > 1) trigger.before(row);
    else trigger.replaceWith(row);
  }

  // Plain (not engraved) units of a variant, as drawn in the drawer right now.
  const plainUnitsInDrawer = (variant) =>
    [...document.querySelectorAll(`[data-vb-eg-open][data-variant="${variant}"]`)].reduce(
      (sum, el) => sum + (Number(el.dataset.qty) || 0),
      0
    );

  /* ---------- bottom sheet ---------- */

  let sheet = null;
  let current = null;
  let busy = false;

  const icon = (d) =>
    `<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="${d}" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;

  function buildSheet() {
    const el = document.createElement('dialog');
    el.className = 'vb-eg-sheet';
    el.setAttribute('aria-labelledby', 'vb-eg-title');
    el.innerHTML = `
      <form class="vb-eg-sheet__panel" novalidate>
        <div class="vb-eg-sheet__head">
          <p class="vb-eg-sheet__title" id="vb-eg-title"></p>
          <button type="button" class="vb-eg-sheet__close" data-vb-eg-close aria-label="Close">${icon('M4 4l8 8M12 4l-8 8')}</button>
        </div>
        <div class="vb-eg-sheet__preview">
          <img alt="" width="600" height="600" decoding="async">
          <span class="vb-eg-sheet__live" aria-hidden="true"></span>
        </div>
        <p class="vb-eg-sheet__for"></p>
        <label class="vb-eg-sheet__field">
          <span class="vb-eg-sheet__sr"></span>
          <input type="text" name="vb-eg-text" autocomplete="off" autocorrect="off" autocapitalize="words" spellcheck="false" enterkeyhint="done">
        </label>
        <p class="vb-eg-sheet__meta">
          <span class="vb-eg-sheet__count" aria-live="polite"></span>
          <span class="vb-eg-sheet__note"></span>
        </p>
        <p class="vb-eg-sheet__error" role="alert" hidden></p>
        <button type="submit" class="vb-eg-sheet__cta" disabled></button>
      </form>`;

    const form = el.querySelector('form');
    const input = el.querySelector('input');

    input.addEventListener('input', () => {
      const clean = input.value
        .replace(ALLOWED, '')
        .replace(/\s{2,}/g, ' ')
        .replace(/^\s+/, '')
        .slice(0, current.max);
      if (clean !== input.value) input.value = clean;
      render();
    });

    // On a phone, "Done" closes the keyboard so the preview and the button are
    // seen before adding; on desktop, Enter adds.
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && window.matchMedia('(pointer: coarse)').matches) {
        e.preventDefault();
        input.blur();
      }
    });

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      submit();
    });

    el.querySelector('[data-vb-eg-close]').addEventListener('click', () => el.close());

    // Click on the backdrop (the dialog box itself, outside the panel) closes.
    el.addEventListener('click', (e) => {
      if (e.target === el) el.close();
    });

    // Escape, close button, backdrop, success: focus goes back to the drawer.
    el.addEventListener('close', () => {
      unfollowKeyboard();
      if (current && current.trigger && current.trigger.isConnected) current.trigger.focus();
    });

    document.body.appendChild(el);
    return el;
  }

  // iOS Safari and Chrome Android shrink only the visual viewport when the
  // keyboard opens: lift the sheet by the hidden part so the button stays visible.
  const vv = window.visualViewport;
  function placeSheet() {
    if (!sheet || !vv) return;
    const hidden = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    sheet.style.setProperty('--vb-eg-kb', `${Math.round(hidden)}px`);
    sheet.style.setProperty('--vb-eg-vh', `${Math.round(vv.height)}px`);
  }
  function followKeyboard() {
    if (!vv) return;
    vv.addEventListener('resize', placeSheet);
    vv.addEventListener('scroll', placeSheet);
    placeSheet();
  }
  function unfollowKeyboard() {
    if (!vv) return;
    vv.removeEventListener('resize', placeSheet);
    vv.removeEventListener('scroll', placeSheet);
    if (sheet) sheet.style.setProperty('--vb-eg-kb', '0px');
  }

  function render() {
    const text = sheet.querySelector('input').value;
    const left = current.max - text.length;
    const live = sheet.querySelector('.vb-eg-sheet__live');
    live.textContent = text || current.sample;
    live.classList.toggle('is-sample', !text);
    sheet.querySelector('.vb-eg-sheet__count').textContent = `${left} character${left === 1 ? '' : 's'} left`;
    sheet.querySelector('.vb-eg-sheet__cta').disabled = busy || !text.trim();
  }

  function openSheet(trigger) {
    if (sheet && sheet.open) return;
    const c = config();
    if (!sheet || !sheet.isConnected) sheet = buildSheet();

    current = {
      key: trigger.dataset.key,
      variant: Number(trigger.dataset.variant),
      qty: Number(trigger.dataset.qty) || 1,
      for: trigger.dataset.for || '',
      price: trigger.dataset.price || '',
      max: Number(c.max) || 9,
      sample: c.sample || 'Your name',
      cta: c.cta || 'Add personalization',
      error: c.error || 'Something went wrong. Please try again.',
      trigger,
    };

    sheet.style.setProperty('--vb-eg-color', c.color || '#949394');
    const inner = document.querySelector('cart-drawer .drawer__inner');
    const width = inner ? Math.round(inner.getBoundingClientRect().width) : 0;
    sheet.style.setProperty('--vb-eg-w', width > 0 ? `${width}px` : '100vw');

    sheet.querySelector('.vb-eg-sheet__title').textContent = c.title || 'Personalize your case';
    sheet.querySelector('.vb-eg-sheet__for').textContent = current.for;
    sheet.querySelector('.vb-eg-sheet__note').textContent = c.note || '';
    sheet.querySelector('.vb-eg-sheet__sr').textContent = c.label || 'Engraving text';

    const img = sheet.querySelector('img');
    img.src = trigger.dataset.image;
    img.alt = current.for;

    const input = sheet.querySelector('input');
    input.value = '';
    input.maxLength = current.max;
    input.placeholder = c.placeholder || 'Type a name';

    busy = false;
    const cta = sheet.querySelector('.vb-eg-sheet__cta');
    cta.classList.remove('is-busy');
    // Exactly the editor label; "[price]" in it is optional, the pill already shows the price.
    cta.textContent = current.cta.replace('[price]', current.price);
    showError('');
    render();

    sheet.showModal();
    followKeyboard();
    // Desktop only: on a phone the keyboard would cover the preview mid-animation.
    if (window.matchMedia('(pointer: fine)').matches) input.focus();
  }

  function showError(message) {
    const box = sheet.querySelector('.vb-eg-sheet__error');
    box.textContent = message;
    box.hidden = !message;
  }

  const plainOf = (cart, variant) =>
    cart.items.filter((i) => i.variant_id === variant && !nameOf(i)).reduce((sum, i) => sum + i.quantity, 0);

  const engravedOf = (cart, variant, text) =>
    cart.items.filter((i) => i.variant_id === variant && nameOf(i) === text).reduce((sum, i) => sum + i.quantity, 0);

  // Always 2 cart writes, whatever the cart looks like (each write costs ~0.9 s on
  // Shopify's side because the automatic BOGO discount is recomputed):
  //  1. take the clicked plain unit out, by the key the drawer was just drawn with;
  //  2. one add.js with the engraved unit + the addon (+ any plain unit the first
  //     write took too many: with 3+ units of one color a row key can carry 2 units),
  //     carrying `sections` so the drawer renders from that response.
  // If step 2 fails, the plain unit goes straight back: nothing is lost.
  // Returns a response carrying `sections`, or null (drawer to refresh).
  async function engrave(d, text, plainBefore) {
    const plain = (i) => i.variant_id === d.variant && !nameOf(i);
    let res;
    try {
      res = await post('cart/change.js', { id: d.key, quantity: Math.max(0, d.qty - 1) });
    } catch (e) {
      // Key gone (cart changed elsewhere): take a plain unit on a fresh cart.
      res = await takeUnits(await getCart(), plain, 1, d.key);
    }
    const left = plainOf(res, d.variant);
    if (plainBefore > 0 && left >= plainBefore) throw new Error('No plain unit could be taken out');
    const giveBack = plainBefore > 0 ? plainBefore - 1 - left : 0;

    const addon = Number(config().addon);
    const items = [{ id: d.variant, quantity: 1, properties: { personalized_text: text } }];
    if (addon) items.push({ id: addon, quantity: 1 });
    if (giveBack > 0) items.push({ id: d.variant, quantity: giveBack });
    try {
      return await post('cart/add.js', withSections({ items }));
    } catch (err) {
      await post('cart/add.js', { items: [{ id: d.variant, quantity: 1 + Math.max(0, giveBack) }] }).catch(() => {});
      throw err;
    }
  }

  function submit() {
    const text = sheet.querySelector('input').value.trim();
    if (!text) return;
    const d = current;
    const plainBefore = plainUnitsInDrawer(d.variant);
    sheet.close();
    showPending(d, text);
    enqueue(() =>
      engrave(d, text, plainBefore)
        .then(renderFrom)
        .catch((err) => {
          console.error('[vb-engraving] add', err);
          toast((err && err.description) || d.error);
          refreshDrawer();
        })
    );
  }

  // Also 2 writes: one update.js drops the engraved line AND lowers the addon (both
  // keys as drawn), then add.js puts the plain units back and renders the drawer.
  async function removeEngraving(r) {
    const engraved = (i) => i.variant_id === r.variant && nameOf(i) === r.text;
    let res = null;
    try {
      res =
        r.addonKey && r.addonLines === 1
          ? await post('cart/update.js', { updates: { [r.key]: 0, [r.addonKey]: Math.max(0, r.addonQty - r.qty) } })
          : await post('cart/change.js', { id: r.key, quantity: 0 });
    } catch (e) {
      res = null;
    }
    const removed = res ? r.before - engravedOf(res, r.variant, r.text) : 0;
    if (removed <= 0) {
      // Keys gone (cart changed elsewhere): safe slow path, plain units back first.
      await post('cart/add.js', { items: [{ id: r.variant, quantity: r.qty }] });
      const cart = await takeUnits(await getCart(), engraved, r.qty, r.key);
      await reconcile(cart);
      return null;
    }
    const back = { items: [{ id: r.variant, quantity: removed }] };
    try {
      return await post('cart/add.js', withSections(back));
    } catch (err) {
      return post('cart/add.js', withSections(back)); // one retry: never leave a unit out
    }
  }

  /* ---------- delegated clicks ---------- */

  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-vb-eg-open]');
    if (open) {
      e.preventDefault();
      openSheet(open);
      return;
    }

    const remove = e.target.closest('[data-vb-eg-remove]');
    if (remove) {
      e.preventDefault();
      if (remove.disabled) return;
      remove.disabled = true;
      remove.closest('.vb-eg__done')?.classList.add('is-busy');
      const r = {
        key: remove.dataset.key,
        variant: Number(remove.dataset.variant),
        qty: Number(remove.dataset.qty) || 1,
        text: (remove.dataset.text || '').trim(),
        addonKey: remove.dataset.addonKey || '',
        addonQty: Number(remove.dataset.addonQty) || 0,
        addonLines: Number(remove.dataset.addonLines) || 0,
      };
      // Engraved units with this name, as drawn (a split row pair can share a name).
      r.before = [...document.querySelectorAll('[data-vb-eg-remove]')]
        .filter((b) => Number(b.dataset.variant) === r.variant && (b.dataset.text || '').trim() === r.text)
        .reduce((sum, b) => sum + (Number(b.dataset.qty) || 0), 0);
      enqueue(() =>
        removeEngraving(r)
          .then(renderFrom)
          .catch((err) => {
            console.error('[vb-engraving] remove', err);
            refreshDrawer();
          })
      );
    }
  });

  /* ---------- drift clean-up ---------- */

  let fixing = false;
  let failed = '';

  function runFixes() {
    if (fixing) return;
    const node = document.querySelector('[data-vb-eg-fix]');
    if (!node) {
      // Cart is consistent again: a later identical drift may be fixed again.
      if (ON_CART_PAGE) {
        try {
          sessionStorage.removeItem('vb-eg-fix');
        } catch (e) {}
      }
      return;
    }
    const signature = node.dataset.vbEgFix;
    node.remove();
    // A fix that just failed is not retried for the same state: no request loop.
    if (signature === failed) return;
    // On /cart a fix reloads the page: one reload per state, never a loop.
    if (ON_CART_PAGE) {
      try {
        if (sessionStorage.getItem('vb-eg-fix') === signature) return;
        sessionStorage.setItem('vb-eg-fix', signature);
      } catch (e) {
        return;
      }
    }

    fixing = true;
    getCart()
      .then(reconcile)
      .then(() => {
        failed = '';
        refreshDrawer();
      })
      .catch((err) => {
        failed = signature;
        console.error('[vb-engraving] clean-up', err);
      })
      .finally(() => {
        fixing = false;
      });
  }

  // Warm the preview images while the drawer is on screen: the sheet opens on a loaded image.
  const preloaded = new Set();
  function preload() {
    document.querySelectorAll('[data-vb-eg-open][data-image]').forEach((el) => {
      const src = el.dataset.image;
      if (!src || preloaded.has(src)) return;
      preloaded.add(src);
      new Image().src = src;
    });
  }

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    // setTimeout, not requestAnimationFrame: rAF never fires in a background tab.
    setTimeout(() => {
      queued = false;
      runFixes();
      preload();
    }, 50);
  };

  const watch = () => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) new MutationObserver(schedule).observe(drawer, { childList: true, subtree: true });
    schedule();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
})();
